// Suspend whole classes (e.g. P1, P2) for students without deleting their access records.
// Stored in Firestore settings/classAccess { suspended: ["P1","P2"] } — readable by signed-in users, admin writes.
import React, { useEffect, useState } from "react";
import { db } from "./firebase";
import { doc, getDoc, setDoc, getDocs, collection, writeBatch } from "firebase/firestore";

const LABELS = { P1: "PUC-I", P2: "PUC-II", E1: "Engineering Physics" };

export async function loadSuspended() {
  try { const s = await getDoc(doc(db, "settings", "classAccess")); return (s.exists() && s.data().suspended) || []; }
  catch { return []; }
}

export function SuspendManager({ onChange } = {}) {
  const [sus, setSus] = useState([]);
  const [counts, setCounts] = useState({});
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const cur = await loadSuspended(); setSus(cur); onChange && onChange(cur);
    try {
      const acc = await getDocs(collection(db, "studentAccess"));
      const c = {}; acc.docs.forEach(d => (d.data().access || []).forEach(a => { c[a] = (c[a] || 0) + 1; }));
      setCounts(c);
      const reqs = await getDocs(collection(db, "accessRequests"));
      setPending(reqs.docs.map(d => ({ id: d.id, ...d.data() })).filter(r => r.status === "pending"));
    } catch (e) { console.error(e); }
  };
  useEffect(() => { refresh(); }, []);

  const toggle = async code => {
    const on = sus.includes(code);
    const msg = on
      ? `Restore access to ${LABELS[code]}? Students who were approved before will see it again.`
      : `Suspend ${LABELS[code]}? ${counts[code] || 0} student(s) will lose access immediately (their records are kept, so you can restore later). New requests will be blocked.`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const next = on ? sus.filter(c => c !== code) : [...sus, code];
      await setDoc(doc(db, "settings", "classAccess"), { suspended: next, updatedAt: new Date().toISOString() }, { merge: true });
      setSus(next); onChange && onChange(next);
    } catch (e) { alert("Error: " + e.message); }
    setBusy(false);
  };

  const blocked = pending.filter(r => sus.includes(r.classCode));
  const rejectBlocked = async () => {
    if (!window.confirm(`Reject ${blocked.length} pending request(s) for suspended classes?`)) return;
    setBusy(true);
    try {
      const now = new Date().toISOString();
      for (let i = 0; i < blocked.length; i += 400) {
        const b = writeBatch(db);
        blocked.slice(i, i + 400).forEach(r => b.update(doc(db, "accessRequests", r.id), { status: "rejected", reviewedAt: now, note: "class suspended" }));
        await b.commit();
      }
      refresh();
    } catch (e) { alert("Error: " + e.message); }
    setBusy(false);
  };

  return (
    <div className="suspend-box">
      <h4>⛔ Class access switch</h4>
      <p className="suspend-hint">Suspending a class hides its content from <b>all students</b> at once and blocks new requests. Access records are kept, so you can restore anytime. Admin and teachers are not affected.</p>
      <div className="suspend-row">
        {["P1", "P2", "E1"].map(code => {
          const on = sus.includes(code);
          return (
            <button key={code} disabled={busy} onClick={() => toggle(code)} className={on ? "suspend-chip off" : "suspend-chip on"}>
              <span className="suspend-name">{LABELS[code]}</span>
              <span className="suspend-state">{on ? "⛔ Suspended" : "✅ Open"}</span>
              <span className="suspend-count">{counts[code] || 0} granted</span>
            </button>
          );
        })}
      </div>
      {blocked.length > 0 && (
        <div className="suspend-pending">
          {blocked.length} pending request(s) for suspended classes.
          <button disabled={busy} onClick={rejectBlocked}>Reject them</button>
        </div>
      )}
    </div>
  );
}
